import { useDispatch, useSelector } from 'react-redux';

// libraries
import { Typography } from '@interstate/components/Typography';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import { useSingleTradeInFormProvider } from '../MultiTradeIn/TradeInFormProvider';

// components
import { SelectInput } from './TradeInSelectInput';

// styling
import { DetailContainer } from '../trade.interstate.style';

// consts
import { VEHICLE_CONDITION, ZERO_WIDTH_SPACE } from '../../../constants';
import { TRADE_IN_CONDITION } from '@makemydeal/dr-dash-components';

interface VehicleConditionFieldProps {
    hasTradeIn: boolean;
    isManualFormOpen: boolean;
    disabled?: boolean;
}

export const VehicleConditionField: React.FC<VehicleConditionFieldProps> = ({ hasTradeIn, isManualFormOpen, disabled }) => {
    const dispatch = useDispatch();
    const { tradeInIndex } = useSingleTradeInFormProvider();
    const vehicleCondition = useSelector(tradeInsSelectors.getCondition(tradeInIndex));
    const tradeInVehicle = useSelector(tradeInsSelectors.getTradeInComponentVehicle(tradeInIndex));

    const getVehicleConditionOptions = () => VEHICLE_CONDITION.map((condition) => ({ label: condition, value: condition })).sort();

    const onConditionChange = (event: InterstateOnChangeEvent<SelectInputEventValue>) => {
        const condition = event.target.value as string;
        dispatch(tradeInsActionCreators.updateTradeInCondition(condition, tradeInIndex));
        dispatch(tradeInsActionCreators.updateVehicle({ ...tradeInVehicle, condition }, tradeInIndex));
    };

    return (
        <>
            {isManualFormOpen ? (
                <SelectInput
                    data-testid="trade-condition-select-input"
                    label={TRADE_IN_CONDITION}
                    name={TRADE_IN_CONDITION}
                    onChange={onConditionChange}
                    options={getVehicleConditionOptions()}
                    value={vehicleCondition}
                    placeholder="Select"
                    displayDeselectOption={true}
                    size="sm"
                    disabled={disabled}
                />
            ) : (
                <>
                    {hasTradeIn && (
                        <DetailContainer>
                            <Typography variant="label-md" color="sem.color.on-surface.muted" sx={{ marginBottom: '8px' }}>
                                {TRADE_IN_CONDITION}
                            </Typography>
                            <Typography variant="body-md" tag="span" data-testid="trade-vehicle-condition-value-label">
                                {vehicleCondition || ZERO_WIDTH_SPACE}
                            </Typography>
                        </DetailContainer>
                    )}
                </>
            )}
        </>
    );
};

export default VehicleConditionField;
